import React, { useState, useEffect } from "react";
import { useDraggable } from "@dnd-kit/core";
import { Card, Typography, Tag, Space, Divider } from "antd";
import {
  UserOutlined,
  CheckSquareOutlined,
  PhoneOutlined,
  MailOutlined,
  WhatsAppOutlined,
  UserAddOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};
const getColorFranja = (avisoActivo, colorEtapa) => {
  if (!avisoActivo) return null;
  if (avisoActivo.tipo === "finalizacion") return "#ff4d4f";
  const total = avisoActivo.totalAvisos || 1;
  const alpha = 0.2 + (avisoActivo.indiceAviso / Math.max(total - 1, 1)) * 0.8;
  return hexToRgba(colorEtapa, alpha);
};

const DraggableCard = ({
  id,
  lead,
  isDragDisabled,
  colorEtapa,
  probabilidad,
  onVerDetalle,
  mostrarDetalles,
  avisoActivo,
  onAbrirWhatsapp,
  esMaster,
  onAsignar,
}) => {
  const [expandido, setExpanded] = useState(mostrarDetalles);

  useEffect(() => {
    setExpanded(mostrarDetalles);
  }, [mostrarDetalles]);

  const estaPerdido = lead.estado === 0;

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id,
      disabled: isDragDisabled || estaPerdido,
      data: { lead },
    });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 999 : 1,
        opacity: isDragging ? 0.4 : 1,
      }
    : undefined;

  const valorPonderado = lead.valorPonderado ?? 0;
  const tareasValidas = lead.tareas?.filter((t) => t.tareaId) || [];
  const tareasTotales = tareasValidas.length;
  const tareasCompletadas =
    tareasValidas.filter((t) => t.tareaId.completado).length;

  const telefonoMostrar = lead.numero || lead.contactoId?.numero;
  const correoMostrar = lead.correo || lead.contactoId?.correo;

  const whatsappNoLeidos = lead.whatsappNoLeidos ?? 0;
  const mostrarBotonWhatsapp = !!telefonoMostrar && !!onAbrirWhatsapp;

  const handleRightClick = (e) => {
    e.preventDefault();
    setExpanded(!expandido);
  };

  const detenerArrastre = (e) => e.stopPropagation();
  const handleAbrirWhatsapp = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onAbrirWhatsapp && onAbrirWhatsapp(lead);
  };

  const botonWhatsapp = mostrarBotonWhatsapp ? (
    <span
      onPointerDown={detenerArrastre}
      onMouseDown={detenerArrastre}
      onClick={handleAbrirWhatsapp}
      title={
        whatsappNoLeidos > 0
          ? `${whatsappNoLeidos} mensaje(s) sin leer`
          : "Abrir WhatsApp"
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        flexShrink: 0,
        cursor: "pointer",
        height: 20,
        padding: whatsappNoLeidos > 0 ? "0 7px" : "0 5px",
        borderRadius: 10,
        fontSize: 11,
        fontWeight: "bold",
        lineHeight: "20px",
        color: "#ffffff",
        backgroundColor: whatsappNoLeidos > 0 ? "#25D366" : "#9bd6ad",
      }}
    >
      <WhatsAppOutlined style={{ fontSize: 13 }} />
      {whatsappNoLeidos > 0 && <span>{whatsappNoLeidos}</span>}
    </span>
  ) : null;

  const handleAsignar = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onAsignar(lead);
  };

  const botonAsignar =
    esMaster && onAsignar ? (
      <span
        onPointerDown={detenerArrastre}
        onMouseDown={detenerArrastre}
        onDoubleClick={detenerArrastre}
        onClick={handleAsignar}
        title="Asignar responsable"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          flexShrink: 0,
          marginLeft: "auto",
          cursor: "pointer",
          height: 20,
          padding: "0 7px",
          borderRadius: 10,
          fontSize: 10,
          fontWeight: "bold",
          lineHeight: "18px",
          color: "#1d39c4",
          backgroundColor: "#f0f5ff",
          border: "1px solid #adc6ff",
        }}
      >
        <UserAddOutlined style={{ fontSize: 12 }} />
        <span>Asignar</span>
      </span>
    ) : null;

  const colorFranja = getColorFranja(avisoActivo, colorEtapa);
  const esVencido = avisoActivo?.tipo === "finalizacion";

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        cursor: estaPerdido
          ? "not-allowed"
          : isDragDisabled
            ? "not-allowed"
            : "grab",
        opacity: estaPerdido ? 0.45 : 1,
      }}
      {...listeners}
      {...attributes}
    >
      <Card
        size="small"
        hoverable={!estaPerdido}
        bodyStyle={{ padding: "10px" }}
        style={{
          backgroundColor: esVencido ? "#fff1f0" : "#ffffff",

          borderRadius: "4px",
          boxShadow: isDragging
            ? "0 8px 16px rgba(0,0,0,0.15)"
            : "0 1px 2px rgba(0,0,0,0.05)",
          border: "1px solid #ffffff",
          borderLeft: colorFranja
            ? `5px solid ${colorFranja}`
            : "1px solid #dfe1e6",
        }}
        onContextMenu={handleRightClick}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onVerDetalle && onVerDetalle(lead);
        }}
      >
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          {/* TÍTULO */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 6,
            }}
          >
            <Text
              strong
              style={{ fontSize: "13px", lineHeight: "1.4", color: "#172b4d" }}
            >
              {lead.descripcion}
            </Text>
            {botonWhatsapp}
          </div>

          {expandido ? (
            // Vista detallada
            <>
              {(lead.origen || (esMaster && lead.usuarioId)) && (
                <div style={{ marginTop: "4px", marginBottom: "2px", display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {lead.origen && (
                    <Tag
                      color="#a0a0a0"
                      style={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        margin: 0,
                        padding: "0 4px",
                        lineHeight: "18px",
                      }}
                    >
                      {lead.origen.toUpperCase()}
                    </Tag>
                  )}
                  {esMaster && lead.usuarioId && (
                    <Tag
                      color="geekblue"
                      style={{ fontSize: "10px", margin: 0, padding: "0 4px", lineHeight: "18px" }}
                    >
                      {lead.usuarioId.nombre} {lead.usuarioId.apellidoPaterno ?? ""}
                    </Tag>
                  )}
                </div>
              )}

              <div style={{ marginTop: "4px" }}>
                <Text
                  type="secondary"
                  style={{ fontSize: "11px", display: "block" }}
                >
                  <UserOutlined style={{ marginRight: "4px" }} />
                  {lead.contactoId?.nombre} {lead.contactoId?.apellidoPaterno}{" "}
                  {lead.contactoId?.apellidoMaterno}
                </Text>
                {telefonoMostrar && (
                  <Text
                    type="secondary"
                    style={{ fontSize: "11px", display: "block" }}
                  >
                    <PhoneOutlined style={{ marginRight: "4px" }} />
                    {telefonoMostrar}
                  </Text>
                )}
                {correoMostrar && (
                  <Text
                    type="secondary"
                    style={{ fontSize: "11px", display: "block" }}
                  >
                    <MailOutlined style={{ marginRight: "4px" }} />
                    {correoMostrar}
                  </Text>
                )}
              </div>

              <Divider style={{ margin: "8px 0 4px 0" }} />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  {lead.valor > 0 ? (
                    <>
                      <Text strong style={{ fontSize: "10px", color: "#52c41a" }}>
                        ${valorPonderado?.toLocaleString()}
                      </Text>
                      <Text type="secondary" style={{ fontSize: "10px", marginLeft: 4 }}>
                        /${lead.valor?.toLocaleString()}
                      </Text>
                    </>
                  ) : (
                    <Text type="secondary" style={{ fontSize: "11px" }}>
                      Sin valor
                    </Text>
                  )}
                </div>

                {tareasTotales > 0 && (
                  <Tag
                    color={
                      tareasCompletadas === tareasTotales
                        ? "success"
                        : "default"
                    }
                    style={{
                      margin: 0,
                      fontSize: "10px",
                      border: "none",
                      backgroundColor: "transparent",
                    }}
                  >
                    <CheckSquareOutlined style={{ marginRight: "4px" }} />
                    {tareasCompletadas}/{tareasTotales}
                  </Tag>
                )}
              </div>
            </>
          ) : (
            // Vista simple
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "4px",
                }}
              >
                <Text
                  type="secondary"
                  style={{ fontSize: "11px", maxWidth: "60%" }}
                  ellipsis
                >
                  <UserOutlined style={{ marginRight: "4px" }} />
                  {lead.contactoId?.nombre} {lead.contactoId?.apellidoPaterno}
                </Text>

                {(lead.origen || (esMaster && lead.usuarioId)) && (
                  <div style={{ display: "flex", gap: 2, alignItems: "center", flexShrink: 0 }}>
                    {lead.origen && (
                      <Tag
                        color="#a0a0a0"
                        style={{ fontSize: "9px", margin: 0, padding: "0 4px" }}
                      >
                        {lead.origen.toUpperCase()}
                      </Tag>
                    )}
                    {esMaster && lead.usuarioId && (
                      <Tag
                        color="geekblue"
                        style={{ fontSize: "9px", margin: 0, padding: "0 4px" }}
                      >
                        {lead.usuarioId.nombre}
                      </Tag>
                    )}
                  </div>
                )}
              </div>

              {lead.valor > 0 && (
                <div style={{ marginTop: "4px" }}>
                  <Text strong style={{ fontSize: "9px", color: "#52c41a" }}>
                    ${valorPonderado?.toLocaleString()}
                  </Text>
                  <Text type="secondary" style={{ fontSize: "9px", marginLeft: 4 }}>
                    /${lead.valor?.toLocaleString()}
                  </Text>
                </div>
              )}
            </>
          )}
          {(avisoActivo || botonAsignar) && (
            <div
              style={{
                marginTop: 6,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {avisoActivo && (
                <>
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                       backgroundColor: esVencido ? "#ff4d4f" : colorEtapa,
                      flexShrink: 0,
                    }}
                  />
                  <Text
                    style={{
                      fontSize: 10,
                      color: esVencido ? "#ff4d4f" : colorEtapa,
                      fontWeight: "bold",
                      letterSpacing: "0.4px",
                    }}
                  >
                    {avisoActivo.tipo === "finalizacion"
                      ? "VENCE HOY"
                      : avisoActivo.descripcion?.toUpperCase()}
                  </Text>
                </>
              )}
              {botonAsignar}
            </div>
          )}
          {estaPerdido && (
            <Tag
              color="default"
              style={{ fontSize: 9, marginTop: 4, opacity: 0.7 }}
            >
              PERDIDO
            </Tag>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default DraggableCard;
